import type React from 'react';

import classes from '../Chat.module.scss';
import type { StudentBulletinTutorResponse } from '../api/routes';
import { formatChatMessagePreview } from '../utils/formatChatMessagePreview';
import { formatDisplayName, formatShortName } from '../utils/formatUserName';
import { isAccessibleChatRoomId } from '../utils/isAccessibleChat';
import { ChatListCard } from './ChatListCard';
import { useGlobalPresence } from '../store/GlobalPresenceContext';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';

interface StudentTutorListProps {
  tutors: StudentBulletinTutorResponse[];
  isLoading: boolean;
  currentUserId: number;
  selectedChatId?: string;
  onTutorSelect: (tutor: StudentBulletinTutorResponse) => void;
}

const mapAvatar = (tutor: StudentBulletinTutorResponse): string | undefined => {
  const token = tutor.other_user.profile_photo_token;
  if (!token) {
    return undefined;
  }
  return `/get_image/${token}`;
};

export const StudentTutorList: React.FC<StudentTutorListProps> = ({
  tutors,
  isLoading,
  currentUserId,
  selectedChatId,
  onTutorSelect,
}) => {
  const { isUserOnline, isReady: presenceReady } = useGlobalPresence();
  const accessibleTutors = tutors.filter(
    (tutor) => tutor.chat_id != null && isAccessibleChatRoomId(tutor.room_id),
  );
  const rootClass = `${classes['chat-rooms-panel']} ${classes['chat-list-sidebar--student']}`;

  if (isLoading) {
    return (
      <div className={rootClass}>
        <div className={classes['chat-list-loading']}>
          <BrandLoader center label='Загрузка...' />
        </div>
      </div>
    );
  }

  if (accessibleTutors.length === 0) {
    return (
      <div className={rootClass}>
        <div className={classes['chat-list-empty']}>
          <p>Нет репетиторов по этой заявке</p>
          <small>Репетиторы появятся после отклика или приглашения</small>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <div className={classes['chat-list-items']}>
        {accessibleTutors.map((tutor) => {
          const chatId = tutor.chat_id != null ? String(tutor.chat_id) : undefined;
          const person = {
            name: tutor.other_user.name,
            surname: tutor.other_user.surname,
            patronymic: tutor.other_user.patronymic,
          };
          const otherId = tutor.other_user.id_user ?? tutor.tutor_id;
          const lastAt = tutor.last_message?.date_send;
          const time = lastAt
            ? new Date(lastAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
            : null;

          return (
            <ChatListCard
              key={`${tutor.tutor_id}-${tutor.bulletin_response_id ?? tutor.student_proposal_id}`}
              name={formatShortName(person, tutor.tutor_id)}
              fullName={formatDisplayName(person, tutor.tutor_id)}
              avatar={mapAvatar(tutor)}
              online={presenceReady ? isUserOnline(otherId) : Boolean(tutor.other_user.is_online)}
              preview={formatChatMessagePreview(
                tutor.last_message?.msg,
                tutor.last_message?.user_id,
                currentUserId,
              )}
              time={time}
              unread={tutor.unread_count}
              selected={chatId != null && selectedChatId === chatId}
              accent='student'
              onSelect={() => onTutorSelect(tutor)}
            />
          );
        })}
      </div>
    </div>
  );
};
